import React, {useContext, useEffect, useState} from "react";
import {AiProviderConnectionServiceContext, AppContextContext} from "../../Contexts";
import {Breadcrumb, Button, Descriptions, Form, Input, message, Modal, Select, Skeleton} from "antd";
import * as globalStyles from "../App.module.css";
import {Link, useNavigate, useParams} from "react-router-dom";
import {DocumentTitle} from "../DocumentTitle";
import {useForm} from "antd/es/form/Form";
import AiProviderConnection, {AiProviderConnectionType, formatAiProviderConnectionType} from "../../domain/AiProviderConnection";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";

function AiProviderConnectionDetail() {
    const appContext = useContext(AppContextContext);
    const aiProviderConnectionService = useContext(AiProviderConnectionServiceContext);
    const navigate = useNavigate();
    const {aiProviderConnectionId} = useParams<{ aiProviderConnectionId: string }>();
    const [aiProviderConnection, setAiProviderConnection] = useState<AiProviderConnection | undefined>();
    const [editMode, setEditMode] = useState(false);
    const [form] = useForm();

    const layout = {
        labelCol: {span: 6},
        wrapperCol: {span: 18},
    };

    const tailLayout = {
        wrapperCol: {offset: 6, span: 18},
    };

    const title = aiProviderConnectionId ? (aiProviderConnection?.name || "AI provider connection") : "New AI provider connection";

    useEffect(() => {
        if (aiProviderConnectionId) {
            reload();
        } else {
            form.setFieldsValue({
                type: AiProviderConnectionType.OPENAI_COMPATIBLE_API
            });
            setEditMode(true);
        }
    }, [aiProviderConnectionId]);

    if (aiProviderConnectionId && !aiProviderConnection) {
        return <Skeleton/>;
    }

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title: <Link to={"/definitions/ai-provider-connections"}>AI provider connections</Link>},
                                {title}
                            ]}
                />

                <h1>{title}</h1>

                {aiProviderConnectionId &&
                    <div className={globalStyles["common__top-button-bar"]}>
                        <Button type={"primary"} onClick={() => setEditMode(!editMode)}>
                            Edit
                        </Button>
                        <Button
                            danger
                            style={{marginLeft: 8}}
                            onClick={() => {
                                Modal.confirm({
                                    content: "Do you really want to delete this AI provider connection?",
                                    okText: "Delete",
                                    cancelText: "Cancel",
                                    okButtonProps: {danger: true},
                                    onOk: () => onDelete()
                                });
                            }}>
                            Delete
                        </Button>
                    </div>
                }

                {(!aiProviderConnectionId || editMode) && (
                    <>
                        {aiProviderConnectionId && <h3>Edit AI provider connection</h3>}

                        <Form
                            {...layout}
                            form={form}
                            onFinish={onFinish}
                            className={`${globalStyles["common__form"]} ${aiProviderConnectionId ? globalStyles["common_form--edit"] : globalStyles["common_form--add"]}`}
                        >
                            <Form.Item name="name" label="Name" rules={[{required: true, message: "Name is required."}]}>
                                <Input maxLength={100}/>
                            </Form.Item>

                            <Form.Item name="description" label="Description">
                                <Input.TextArea rows={3} maxLength={1000}/>
                            </Form.Item>

                            <Form.Item name="type" label="Type" rules={[{required: true, message: "Type is required."}]}>
                                <Select
                                    options={[
                                        {
                                            value: AiProviderConnectionType.OPENAI_COMPATIBLE_API,
                                            label: formatAiProviderConnectionType(AiProviderConnectionType.OPENAI_COMPATIBLE_API)
                                        }
                                    ]}
                                />
                            </Form.Item>

                            <Form.Item name="baseUrl" label="Base URL" rules={[{required: true, message: "Base URL is required."}]}>
                                <Input maxLength={1000} placeholder="https://api.example.com/v1"/>
                            </Form.Item>

                            <Form.Item
                                name="apiKey"
                                label="API key"
                                extra={aiProviderConnectionId ? "Leave empty to keep the existing API key." : undefined}
                                rules={aiProviderConnectionId ? [] : [{required: true, message: "API key is required."}]}
                            >
                                <Input.Password maxLength={2000}/>
                            </Form.Item>

                            <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                                <Button type="primary" htmlType="submit">
                                    Save
                                </Button>
                                <Button
                                    style={{marginLeft: 8}}
                                    onClick={() => {
                                        if (aiProviderConnectionId) {
                                            setEditMode(false);
                                            if (aiProviderConnection) {
                                                form.setFieldsValue(toFormValues(aiProviderConnection));
                                            }
                                        } else {
                                            navigate("/definitions/ai-provider-connections");
                                        }
                                    }}>
                                    Cancel
                                </Button>
                            </Form.Item>
                        </Form>
                    </>
                )}

                {aiProviderConnectionId && aiProviderConnection &&
                    <Descriptions column={1} bordered={true} size="small" className={globalStyles.details}>
                        <Descriptions.Item label="Name">{aiProviderConnection.name}</Descriptions.Item>
                        <Descriptions.Item label="Description">
                            {aiProviderConnection.description || <i>no description</i>}
                        </Descriptions.Item>
                        <Descriptions.Item label="Owner">
                            {aiProviderConnection.owner === ResourceOwnerType.SYSTEM
                                ? <i>System connection</i>
                                : aiProviderConnection.organization?.name}
                        </Descriptions.Item>
                        <Descriptions.Item label="Type">{formatAiProviderConnectionType(aiProviderConnection.type)}</Descriptions.Item>
                        <Descriptions.Item label="Base URL">{aiProviderConnection.baseUrl}</Descriptions.Item>
                    </Descriptions>
                }
            </>
        </DocumentTitle>
    );

    function reload() {
        if (!aiProviderConnectionId) {
            return;
        }

        aiProviderConnectionService.get(aiProviderConnectionId)
            .then(value => {
                setAiProviderConnection(value);
                form.setFieldsValue(toFormValues(value));
            });
    }

    function toFormValues(value: AiProviderConnection) {
        return {
            ...value,
            apiKey: undefined
        };
    }

    function onFinish(values: any) {
        if (aiProviderConnectionId) {
            aiProviderConnectionService.update(aiProviderConnectionId, values)
                .then(() => {
                    message.success("AI provider connection updated.");
                    setEditMode(false);
                    reload();
                });
        } else {
            aiProviderConnectionService.add(values)
                .then(() => {
                    message.success("AI provider connection added.");
                    navigate("/definitions/ai-provider-connections");
                });
        }
    }

    function onDelete() {
        if (!aiProviderConnection) {
            return;
        }

        aiProviderConnectionService.delete(aiProviderConnection)
            .then(() => {
                message.success("AI provider connection deleted.");
                navigate("/definitions/ai-provider-connections");
            });
    }
}

export default AiProviderConnectionDetail;
